<template>
  <main role="main" class="container mx-auto mt-16 md:w-full min-h-[81.8vh]">
    <section class="py-5 md:pt-12 md:pb-15">
      <h1
        class="font-medium text-[22px] leading-normal md:text-[40px] md:leading-[50px] text-neutral-900 mb-8"
      >
        Sitemap
      </h1>

      <div
        class="flex flex-col md:flex-row md:flex-nowrap gap-8 md:gap-12 lg:gap-16"
      >
        <div
          v-for="(column, columnIndex) in sitemapColumns"
          :key="columnIndex"
          class="w-full md:w-1/3"
        >
          <div
            v-for="(item, itemIndex) in column"
            :key="itemIndex"
            class="mb-10"
          >
            <h2>
              <NuxtLink
                v-if="item.url"
                :to="item.url"
                class="font-medium text-lg md:text-xl text-neutral-900 hover:text-neutral-600 hover:underline transition-colors duration-200"
              >
                {{ item.parent }}
              </NuxtLink>
              <span
                v-else
                class="font-medium text-lg md:text-xl text-neutral-900"
              >
                {{ item.parent }}
              </span>
            </h2>
            <ul v-if="item.children?.length" class="mt-4 space-y-3">
              <li
                v-for="(child, childIndex) in item.children"
                :key="childIndex"
              >
                <NuxtLink
                  :to="child.url"
                  class="text-sm md:text-base text-neutral-600 hover:text-neutral-900 hover:underline transition-colors duration-200"
                >
                  {{ child.label }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner
const SEOMeta = ref({})

const sitemapColumns = ref([
  [
    {
      parent: 'Services',
      url: '/services',
      children: []
    },
    {
      parent: 'Products',
      url: '/products',
      children: []
    }
  ],
  [
    {
      parent: 'Industries',
      url: '/industries',
      children: []
    },
    {
      parent: 'Solutions',
      url: '/solutions',
      children: []
    },
    {
      parent: 'Work',
      children: [{ label: 'Portfolio', url: '/work' }]
    },
    {
      parent: 'Insights',
      url: '/insights',
      children: [
        { label: 'Articles', url: '/ideas' },
        { label: 'Videos', url: '/videos' },
        { label: 'Podcasts', url: '/podcasts' }
      ]
    }
  ],
  [
    {
      parent: 'Company',
      children: [
        { label: 'About', url: '/about' },
        { label: 'Purpose', url: '/about/purpose' },
        { label: 'Approach', url: '/about/approach' },
        { label: 'History', url: '/about/history' },
        { label: 'Team', url: '/about/team' }
      ]
    },
    {
      parent: 'Careers',
      children: [
        { label: 'Job Openings', url: '/careers' },
        { label: 'Culture', url: '/careers/culture' }
      ]
    },
    {
      parent: 'Sustainability',
      children: [{ label: 'Our Commitment', url: '/sustainability' }]
    }
  ]
])

const route = useRoute()

const getData = async (apiPath, columnIndex, parentKey, urlPath, transform) => {
  const query = {}

  if (apiPath === 'expertices') {
    query['populate[expertice_category][fields][0]'] = 'title'
    query['populate[expertice_category][fields][1]'] = 'slug'
  }

  if (apiPath === 'solutions') {
    query['sort[0]'] = 'order'
  } else {
    query['sort[0]'] = 'title:asc'
  }

  const { data, error } = await useSuitmediaApiData(`/api/${apiPath}`, {
    query
  })

  if (error.value) return

  const itemIndex = sitemapColumns.value[columnIndex].findIndex(
    item => item.parent === parentKey
  )
  if (itemIndex !== -1) {
    const path = urlPath || apiPath
    sitemapColumns.value[columnIndex][itemIndex].children = transform
      ? transform(data.value.data)
      : data.value.data.map(item => ({
          label: item.attributes.title,
          url: `/${path}/${item.attributes.slug}`
        }))
  }
}

const [seoResult] = await Promise.all([
  useSuitmediaApiData('/api/seo-metas', {
    query: {
      'filters[url]': route.path,
      populate: 'seo.image'
    }
  }),
  getData('expertices', 0, 'Services', 'expertises'),
  getData('products', 0, 'Products'),
  getData('industries', 1, 'Industries'),
  getData('solutions', 1, 'Solutions')
])

SEOMeta.value = seoResult.data.value.data?.[0]

const meta = computed(() => {
  const seo = SEOMeta.value?.attributes?.seo || {}
  const url = SEOMeta.value?.attributes?.url || 'suitmedia.com'
  const title = seo.seo_title || 'Suitmedia'
  const description = seo.seo_description || 'Digital Agency Indonesia'
  const image =
    seo.image?.data?.attributes?.url ||
    mainBanner?.attributes?.banner?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
  const ogType = seo.open_graph_type

  return { title, description, url, image, ogType }
})

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: meta.value.title,
  url: meta.value.url,
  description: meta.value.description,
  image: meta.value.image,
  email: 'contact(at)suitmedia.com',
  telephone: '+62 21 719 6877',
  legalName: 'PT Suitmedia Kreasi Indonesia',
  location: 'Jakarta, Bandung, Yogyakarta, Singapore',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Jakarta, Indonesia',
    postalCode: '12510',
    streetAddress: 'Jl. Pejaten Barat II No. 3A'
  }
})

useSeoMeta({
  title: meta.value.title,
  ogTitle: meta.value.title,
  description: meta.value.description,
  ogDescription: meta.value.description,
  ogImage: meta.value.image,
  ogType: meta.value.ogType,
  ogSiteName: meta.value.title,
  twitterTitle: meta.value.title,
  twitterDescription: meta.value.description,
  twitterImage: meta.value.image
})
</script>

<style lang="scss" scoped></style>
